/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import {
  ActivityLogType,
  createLogTypeObjectsWithObjectTypes,
  LOG_TYPES_FOR_OBJECTS,
  ActivityLogWildcardRegex,
  ActivityLogWildcardString,
} from 'cio/utils/activity-log-types';
import EnvironmentService from 'cio/services/environment';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import { FlyInputEvent } from '@customerio/fly-components/types/fly-components/events';
import DS from 'ember-data';
import FeaturesService from 'ember-feature-flags/services/features';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';
import ObjectTypeModel from 'cio/models/object-type';

interface CioActivityLogFilterArgs {
  type: string;
  name: Record<string, any>;
  objectTypes?: ObjectTypeModel[];
  objectType?: ObjectTypeModel;
  onNameChange: (name: string | null) => unknown;
  onTypeChange: (type: string | null) => unknown;
  hiddenTypes?: string[];
}

export default class CioActivityLogFilter extends Component<CioActivityLogFilterArgs> {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service features!: FeaturesService;

  @tracked typeObjects: { value: string; label: string }[] = [];
  @tracked showFilters = false;
  @tracked selectedObjectType = this.objectTypeOptions[0]?.value;

  constructor(owner: unknown, args: CioActivityLogFilterArgs) {
    super(owner, args);

    // calling private method in constructor because Ember decorators have issues during runtime
    this.selectedObjectType = this.#getInitiallySelectedObjectType();
    this.typeObjects = this.#setTypeObjects();
  }

  get decodedType() {
    return this.#decodeRelAttrType(this.args.type);
  }

  get hiddenTypes() {
    if (this.args.hiddenTypes != null) {
      return this.args.hiddenTypes;
    }
    if (this.objectTypes.length === 0) {
      // Remove Objects related log types if objects are not enabled or any Object Type does not exist
      return LOG_TYPES_FOR_OBJECTS;
    }
    return [];
  }

  get parsedType() {
    return this.args.type.replace(ActivityLogWildcardRegex, ActivityLogWildcardString);
  }

  get objectTypeIdFromType() {
    return this.#getObjectTypeFromType(this.args.type);
  }

  get isRelationshipAttributeType() {
    return [ActivityLogType.RelationshipAttributeChange, ActivityLogType.RelationshipFailedAttributeChange].includes(
      this.parsedType as ActivityLogType,
    );
  }

  get showObjectTypeOptions() {
    return this.args.objectType == null && this.isRelationshipAttributeType;
  }

  @computed(
    'args.type',
    'parsedType',
    'activeObjectType.sentAttributes',
    'environment.{sentAttributes,sentEvents}',
    'selectedObjectType',
  )
  get names() {
    if (this.args.type === ActivityLogType.Event) {
      return this.environment.sentEvents;
    }

    if (this.args.type === ActivityLogType.AttributeChange) {
      return this.environment.sentAttributes;
    }

    if (this.parsedType === ActivityLogType.ObjectAttributeChange) {
      return (
        this.activeObjectType?.sentAttributes ?? [
          ObjectIdentifierKey.CioObjectId,
          'created_at',
          'name',
          ObjectIdentifierKey.ObjectId,
        ]
      );
    }

    if (this.isRelationshipAttributeType) {
      return this.store.peekRecord('object-type', this.selectedObjectType)?.sentRelAttributes ?? [];
    }

    return [];
  }

  get showFilterInputs() {
    if (this.isRelationshipAttributeType) {
      return true;
    }

    return this.names.length > 0;
  }

  get activeObjectType() {
    let { objectTypeIdFromType, objectTypes } = this;
    if (objectTypeIdFromType != null && Number(objectTypeIdFromType) > 0 && objectTypes.length > 0) {
      return objectTypes.find((objectType) => objectType.id === objectTypeIdFromType);
    }
    return;
  }

  get isObject() {
    return this.activeObjectType != null;
  }

  get objectTypes() {
    return this.args.objectTypes ?? [];
  }

  get isFilteredByFreeText() {
    return this.args.type === ActivityLogType.ProfileDelete || this.args.type === ActivityLogType.ProfileCreate;
  }

  @computed('args.type', 'parsedType', 'activeObjectType.moreAttributes', 'environment.{moreAttributes,moreEvents}')
  get moreNames() {
    if (this.args.type === ActivityLogType.Event) {
      return this.environment.moreEvents;
    }

    if (this.args.type === ActivityLogType.AttributeChange) {
      return this.environment.moreAttributes;
    }

    if (this.parsedType === ActivityLogType.ObjectAttributeChange) {
      return this.activeObjectType?.moreAttributes;
    }

    return;
  }

  get typeLabel() {
    let type = this.#decodeRelAttrType(this.args.type);

    if (type === ActivityLogType.ProfileDelete || type === ActivityLogType.ProfileCreate) {
      return 'identities';
    }

    if (
      [
        ActivityLogType.AttributeChange,
        ActivityLogType.ObjectAttributeChange,
        ActivityLogType.RelationshipAttributeChange,
        ActivityLogType.RelationshipFailedAttributeChange,
      ].includes(type as ActivityLogType)
    ) {
      return 'attribute';
    }

    if (type === ActivityLogType.Event) {
      return 'event';
    }

    return 'activity name';
  }

  get objectTypeOptions() {
    return this.store.peekAll('object-type').map((objectType) => {
      return {
        value: objectType.id,
        label: objectType.name,
      };
    });
  }

  @action
  toggleFilters() {
    if (!this.showFilters) {
      this.showFilters = true;
    } else {
      this.showFilters = false;
      this.args.onTypeChange(null);
      this.args.onNameChange('');
    }
  }

  @action
  selectType(opt?: Record<string, any>) {
    let value = opt ? opt.value : null;

    if (value !== this.args.type) {
      this.args.onNameChange('');
    }

    let objectTypeId = this.#getObjectTypeFromType(value);
    if (value != null && value.startsWith('_r') === true && objectTypeId != null) {
      value = value.replace(objectTypeId, this.selectedObjectType);
    }

    this.args.onTypeChange(value);
  }

  @action
  selectName(selection: string | null) {
    return this.args.onNameChange(selection);
  }

  @action
  filterName(event: FlyInputEvent<HTMLInputElement>) {
    let { value } = event.target;
    this.args.onNameChange(value);
  }

  @action
  setShowFilters() {
    if (!isEmpty(this.args.type)) {
      this.showFilters = true;
    }
  }

  @action
  changeObjectType({ value }: (typeof this.objectTypeOptions)[0]) {
    this.selectedObjectType = value;
    this.selectType({ value: this.args.type });
  }

  #getInitiallySelectedObjectType() {
    // look for initial object type in the following order:
    // 1. from args;
    // 2. from filter type (if it is not a wildcard);
    // 3. from first object type option
    let initialType = this.objectTypeIdFromType === '*' ? null : this.objectTypeIdFromType;
    return this.args.objectType?.id ?? initialType ?? this.objectTypeOptions[0]?.value;
  }

  #decodeRelAttrType(type: string) {
    if (this.isRelationshipAttributeType) {
      return type.replace(ActivityLogWildcardRegex, ActivityLogWildcardString);
    }

    return type;
  }

  #setTypeObjects() {
    return createLogTypeObjectsWithObjectTypes(this.objectTypes, this.hiddenTypes).filter((logType) => {
      return (
        !(!this.features.isEnabled('campaign_trigger_ui_new') && logType.value.startsWith('_r')) &&
        !(logType.value === ActivityLogType.OpenedEmail && !isDeliveryMeasureEnabledForChannel('opened', 'email'))
      );
    });
  }

  #getObjectTypeFromType(type: string) {
    if (type == null) {
      return null;
    }

    // because of the (object and relationship activity log) pattern and regex,
    // the id will always be the 4th element of the array
    return type.match(ActivityLogWildcardRegex)?.[3];
  }
}
